import React, { useState, useEffect, useRef, useCallback } from 'react';

import { SignupContinueData, SignupOption, SignupRegion } from '../types';

interface UseSignupScreen2Props {
  erv: string | boolean | undefined;
  name: string | undefined;
  ervFieldName: string;
  departments: SignupOption[] | undefined;
  subDepartments: string[][] | undefined;
  regions: SignupRegion[] | undefined;
  fandf: boolean;
  error: string | undefined;
  isHub: boolean;
  handleContinue: (dataObj: SignupContinueData) => void;
  onOpenModal: (params: { modalText: string; questionTitle: string }) => void;
}

interface UseSignupScreen2Return {
  errorMessage: string | undefined;
  setErrorMessage: (message: string | undefined) => void;
  name: string | undefined;
  setName: (name: string) => void;
  eid: string;
  setEid: (eid: string) => void;
  region: string | undefined;
  setRegion: (region: string | undefined) => void;
  regions: SignupRegion[];
  setRegions: (regions: SignupRegion[]) => void;
  regionGroupArr: SignupOption[];
  setRegionGroupArr: (arr: SignupOption[]) => void;
  regionGroup: string | undefined;
  setRegionGroup: (group: string | undefined) => void;
  regionsOptions: SignupRegion[];
  setRegionsOptions: (options: SignupRegion[]) => void;
  department: string | undefined;
  setDepartment: (dept: string | undefined) => void;
  subDepartment: string | undefined;
  setSubDepartment: (subDept: string | undefined) => void;
  subDepts: SignupOption[];
  setSubDepts: (subDepts: SignupOption[]) => void;
  regionid: number | undefined;
  setRegionid: (id: number | undefined) => void;
  departmentid: number | undefined;
  setDepartmentid: (id: number | undefined) => void;
  subDepartmentid: number | undefined;
  setSubDepartmentid: (id: number | undefined) => void;

  // Field validation errors
  nameError: boolean;
  setNameError: (error: boolean) => void;
  eidError: boolean;
  setEidError: (error: boolean) => void;
  regionGroupError: boolean;
  setRegionGroupError: (error: boolean) => void;
  regionError: boolean;
  setRegionError: (error: boolean) => void;
  departmentError: boolean;
  setDepartmentError: (error: boolean) => void;
  subDepartmentError: boolean;
  setSubDepartmentError: (error: boolean) => void;

  // Handlers
  onSetRegionGroup: (groupRegion: string) => void;
  onSetDept: (dept: string) => void;
  handleRegion: (region: string) => void;
  handleSubDepartment: (subdept: string) => void;
  handleContinue: () => void;

  // Computed state
  isLoading: boolean;

  // Refs for focus management
  nameInputRef: React.RefObject<HTMLInputElement>;
  eidInputRef: React.RefObject<HTMLInputElement>;
}

export const useSignupScreen2 = ({
  erv,
  name: initialName,
  ervFieldName,
  departments,
  subDepartments,
  regions: initialRegions,
  fandf,
  handleContinue: handleContinueProp,
  onOpenModal,
  error,
  isHub,
}: UseSignupScreen2Props): UseSignupScreen2Return => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [name, setName] = useState<string | undefined>(initialName);
  const [eid, setEid] = useState('');
  const [region, setRegion] = useState<string | undefined>();
  const [regions, setRegions] = useState<SignupRegion[]>([]);
  const [regionGroupArr, setRegionGroupArr] = useState([]);
  const [regionGroup, setRegionGroup] = useState<string | undefined>();
  const [regionsOptions, setRegionsOptions] = useState([]);
  const [department, setDepartment] = useState<string | undefined>();
  const [subDepartment, setSubDepartment] = useState<string | undefined>();
  const [subDepts, setSubDepts] = useState([]);
  const [regionid, setRegionid] = useState<number | undefined>();
  const [departmentid, setDepartmentid] = useState<number | undefined>();
  const [subDepartmentid, setSubDepartmentid] = useState<number | undefined>();

  // Field validation errors
  const [nameError, setNameError] = useState(false);
  const [eidError, setEidError] = useState(false);
  const [regionGroupError, setRegionGroupError] = useState(false);
  const [regionError, setRegionError] = useState(false);
  const [departmentError, setDepartmentError] = useState(false);
  const [subDepartmentError, setSubDepartmentError] = useState(false);

  // Refs for focus management
  const nameInputRef = useRef<HTMLInputElement>(null);
  const eidInputRef = useRef<HTMLInputElement>(null);

  // Initialize regions and groups
  useEffect(() => {
    if (initialRegions && initialRegions.length > 1 && initialRegions[0].region_group) {
      const groupArr = [];
      initialRegions.forEach((initialRegion, index) => {
        if (groupArr.filter((el) => el.title === initialRegion.region_group).length === 0) {
          groupArr.push({ id: index, title: initialRegion.region_group });
        }
        setRegionGroupArr(groupArr);
      });
    }
    if (initialRegions && initialRegions.length > 1) {
      setRegions(initialRegions);
    }
    if (initialName) {
      setName(initialName);
    }
  }, [initialRegions, initialName]);

  // Handle external error from parent
  useEffect(() => {
    setErrorMessage(error);
  }, [error]);

  const onSetRegionGroup = useCallback(
    (groupRegion: string) => {
      if (regionGroup && groupRegion !== regionGroup) {
        setRegion(' ');
      }
      const regionsArr = regions?.filter(
        (regionOption) => regionOption.region_group === groupRegion
      );
      setRegionsOptions(regionsArr);
      setRegionGroup(groupRegion);
    },
    [regionGroup, regions]
  );

  const onSetDept = useCallback(
    (dept: string) => {
      setDepartment(dept);
      const obj = departments?.find((el) => el.title === dept);
      if (obj) {
        setDepartmentid(obj.id);
        if (subDepartments && subDepartments.length > 0) {
          if (subDepartment && dept !== departments[Number(obj.id) - 1]?.title) {
            setSubDepartment(' ');
          }
          const subDeptsArr = subDepartments[Number(obj.id) - 1] || [];
          const subDeptsArrOfObj = [];
          subDeptsArr.forEach((subdept, index) => {
            if (subdept.length > 0) {
              subDeptsArrOfObj.push({
                id: index + 1,
                title: subdept,
              });
            }
          });
          setSubDepts(subDeptsArrOfObj);
        }
      }
    },
    [departments, subDepartments, subDepartment]
  );

  const handleRegion = useCallback(
    (regionName: string) => {
      setRegion(regionName);
      const obj = regions?.find((reg) => reg.title === regionName);
      if (obj) {
        setRegionid(obj.id);
      }
    },
    [regions]
  );

  const handleSubDepartment = useCallback(
    (subdept: string) => {
      setSubDepartment(subdept);
      const id = subDepartments?.[departmentid ? departmentid - 1 : 0]?.indexOf(subdept);
      if (id !== undefined && id >= 0) {
        setSubDepartmentid(id + 1);
      }
    },
    [subDepartments, departmentid]
  );

  const handleContinue = useCallback(() => {
    setErrorMessage('');
    // Clear all field errors first
    setNameError(false);
    setEidError(false);
    setRegionGroupError(false);
    setRegionError(false);
    setDepartmentError(false);
    setSubDepartmentError(false);

    let hasErrors = false;

    if (!name) {
      setNameError(true);
      hasErrors = true;
    }
    if (!eid && erv && erv !== 'no' && !fandf && !isHub) {
      setEidError(true);
      hasErrors = true;
    }
    if (!regionid && regions.length > 1) {
      if (regionGroupArr.length > 0) {
        setRegionGroupError(true);
      } else {
        setRegionError(true);
      }
      hasErrors = true;
    }
    if (!departmentid && departments && departments.length > 1 && !fandf) {
      setDepartmentError(true);
      hasErrors = true;
    }
    // error if selected department has more then one subdepartment but none are selected
    if (!subDepartmentid && subDepartments?.[Number(departmentid) - 1]?.length > 1 && !fandf) {
      setSubDepartmentError(true);
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    handleContinueProp({
      progress: 'screen2',
      name,
      regionid,
      departmentid,
      subDepartmentid,
      eid,
    });
  }, [
    name,
    eid,
    erv,
    fandf,
    isHub,
    regionid,
    regions,
    regionGroupArr,
    departments,
    subDepartments,
    departmentid,
    subDepartmentid,
    handleContinueProp,
  ]);

  return {
    // Form state
    errorMessage,
    setErrorMessage,
    name,
    setName,
    eid,
    setEid,
    region,
    setRegion,
    regions,
    setRegions,
    regionGroupArr,
    setRegionGroupArr,
    regionGroup,
    setRegionGroup,
    regionsOptions,
    setRegionsOptions,
    department,
    setDepartment,
    subDepartment,
    setSubDepartment,
    subDepts,
    setSubDepts,
    regionid,
    setRegionid,
    departmentid,
    setDepartmentid,
    subDepartmentid,
    setSubDepartmentid,

    // Field validation errors
    nameError,
    setNameError,
    eidError,
    setEidError,
    regionGroupError,
    setRegionGroupError,
    regionError,
    setRegionError,
    departmentError,
    setDepartmentError,
    subDepartmentError,
    setSubDepartmentError,

    // Handlers
    onSetRegionGroup,
    onSetDept,
    handleRegion,
    handleSubDepartment,
    handleContinue,

    // Computed state
    isLoading: false,

    // Refs
    nameInputRef,
    eidInputRef,
  };
};
